import { html, LitElement, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';
import { TrackableEventAction } from '../../../../app/package/base/service/tracking/tracking-service';
import { type GameBase, TileType } from '../../../base/package/api/package/gaming/gaming-domain';
import '../../ui-game-tile/ui-game-tile';
import styles from './game-tile-deck.css?inline';

const CName = 'incentive-game-tile-deck';
const tileLimit = 3;

type DeckGame = GameBase & { slug: string; labels?: string[]; vid?: string };

@customElement(CName)
export class IncentiveGameTileDeck extends LitElement {
  static readonly styles = unsafeCSS(styles);

  @property({ type: Object }) games: DeckGame[];
  @property({ type: String }) tileType: TileType = TileType.P3;
  @property({ type: Function }) onTileClick: (gameId: string) => void;
  @property({ type: String }) trackingSource?: string;
  @property({ type: String }) variant: 'default' | 'fanned' | 'fanned-large' = 'default';

  render() {
    // we need to show only first 3 games, but render in reverse to avoid collapsing z-index
    const games = this.games.slice(0, tileLimit).reverse();
    const classes = classMap({
      deck: true,
      'deck--fanned': this.variant === 'fanned' || this.variant === 'fanned-large',
      'deck--fanned-large': this.variant === 'fanned-large',
    });
    return html`
      <div class=${classes}>
        ${repeat(
          games,
          (game) => game.id,
          (game) => html`
            <ui-game-tile
              class="deck__tile"
              .incentiveStyle=${this.variant === 'default'}
              .isInfoIconShown=${false}
              .activeUser=${true}
              .infoAction=${() => this._navigateToGameInfo(game.slug)}
              .gameId=${game.id}
              .slug=${game.slug}
              .title=${game.title}
              .imageSrc=${game.img}
              .type=${this.tileType}
              .clickAction=${() => {
                this._trackGameClick(game);
                this.onTileClick?.(game.id);
              }}
            ></ui-game-tile>
          `,
        )}
      </div>
    `;
  }

  private _navigateToGameInfo(slug: string) {
    window.$app.router.navigateToGameInfo(slug);
  }

  private _trackGameClick(game: DeckGame) {
    if (this.trackingSource) {
      window.$app.trackingStore.value.gameSource = this.trackingSource;
    }

    window.$app.content
      .getGameInfo(undefined, game.slug, TileType.L1)
      .then((gameInfo) => {
        const trackingParams = {
          gameCategory: game.labels?.join(' - '),
          gameName: game.title,
          gamePromo: game.vid ? 'video' : 'image',
          gameProvider: gameInfo.getStudio(),
          gameSource: this.trackingSource,
          winType: undefined,
        };
        window.$app.trackingStore.next({ ...window.$app.trackingStore.value, ...trackingParams });
        window.$app.track.gamePlay({ ...trackingParams, eventAction: TrackableEventAction.GAME_SELECTED });
      })
      .catch((err) => {
        window.$app.logger.warn(`Error fetching game info '${game.id}'`, err);
      });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: IncentiveGameTileDeck;
  }
}
